import { MapList } from '../../Config/Levels';
import { Collection } from '../../Tiles/TileSet';
import { MapChestsStates } from '../../Config/MapChests';
import * as fromActions from '../Actions/Actions';
import { ActionTypes } from '../Actions/Actions';
import { getLevelIndex } from './level';
import Purchase = FBInstant.Purchase;
import { StateUtils } from '../../Managers/StateUtils';

export interface IGameMaps {
    [key: string]: IMapData;
}

export interface IMapData {
    [key: string]: IMapLevelData;
}

export interface ICollectablesData {
    [key: string]: number;
}

export interface IMapLevelData {
    id: string;
    stars: number;
    attempts?: number;
}

export interface IStatePlayer {
    activeCollection: Collection;
    collectablesProgress: ICollectablesData;
    gainedBoosters: string[];
    gainedCoins: number;
    gainedHearts: number;
    gainedStars: number;
    gainedTokens: number;
    gainedTokensCollection: string;
    levelId: string;
    mapId: string;
    mapTransition: boolean;
    mapLevels: IMapData;
    prizeWheelCooldown: number;
    lastLevelIndex: number;
    lastInterstitialDate: number;
    replaying: boolean;
    totalStars: number;
    unlocked: string[];
    unlockedChests: MapChestsStates[];
    visited: string[];
    shouldSubscribe: boolean;
    timeSubscribed: number | null;
    optInShortcutRewardDate: number;
    optInShortcutShown: boolean;
    nativeOptInShortcutRequired: boolean;
    shopHasBeenOpened: boolean;
    purchases: Purchase[];
    shopGift: number;
    xPromoDismissed: boolean;
}

export const defaultStatePlayer: IStatePlayer = {
    activeCollection: Collection.DEFAULT,
    collectablesProgress: { default: 100 },
    gainedBoosters: [],
    gainedCoins: 0,
    gainedHearts: 0,
    gainedStars: 0,
    gainedTokens: 0,
    gainedTokensCollection: null,
    lastLevelIndex: 0,
    lastInterstitialDate: 0,
    levelId: MapList[0] + '-1',
    mapId: MapList[0],
    mapLevels: createGameMaps(),
    mapTransition: false,
    prizeWheelCooldown: 0,
    replaying: false,
    shouldSubscribe: false,
    timeSubscribed: null,
    totalStars: 0,
    unlocked: [],
    unlockedChests: [],
    visited: [],
    optInShortcutRewardDate: null,
    optInShortcutShown: false,
    nativeOptInShortcutRequired: false,
    shopHasBeenOpened: false,
    purchases: [],
    shopGift: 0,
    xPromoDismissed: false,
};

export const StatePlayer = (
    state = defaultStatePlayer,
    action: fromActions.Actions
): IStatePlayer => {
    switch (action.type) {
        case ActionTypes.INIT_CORE:
            return {
                ...state,
            };
        case ActionTypes.INTERSTITIAL_AD_WATCHED:
            return {
                ...state,
                lastInterstitialDate: Date.now(),
            };
        case ActionTypes.ADD_CHEST:
            const addChests = state.unlockedChests;
            addChests.push(MapChestsStates.LOCKED);
            return {
                ...state,
                unlockedChests: addChests,
            };
        case ActionTypes.CHANGE_VIEW:
            const last = StateUtils.getNextLevelIdForMap(state);
            const mapLevels = { ...state.mapLevels };

            if (!mapLevels[last]) {
                mapLevels[last] = { id: action.payload, stars: 0 };
            }
            return {
                ...state,
            };
        case ActionTypes.CHANGE_CHEST_STATE:
            const stateChests = state.unlockedChests;
            if (action.payload[0] <= stateChests.length + 1) {
                stateChests[action.payload[0]] = action.payload[1] as MapChestsStates;
            }
            return {
                ...state,
                unlockedChests: stateChests,
            };
        case ActionTypes.CHANGE_MAP:
            return {
                ...state,
                mapId: action.payload,
                // visited: addToVisited(state, action.payload),
            };
        case ActionTypes.RECEIVE_REWARDS:
            return {
                ...state,
                gainedCoins: 0,
                gainedTokens: 0,
                gainedHearts: 0,
                gainedStars: 0,
                gainedBoosters: [],
            };
        case ActionTypes.UNLOCK_MAP:
            const unlocks = state.unlocked.slice(0);
            if (unlocks.indexOf(action.payload) === -1) {
                unlocks.push(action.payload);
            }
            return {
                ...state,
                unlocked: unlocks,
            };

        case ActionTypes.UNLOCK_MAP_LEVEL:
            const all: IMapData = { ...state.mapLevels };
            const newLast = Math.max(state.lastLevelIndex, getLevelIndex(action.payload));
            if (!all[action.payload]) {
                const newLevel = { id: action.payload, stars: 0 };
                all[action.payload] = newLevel;
            }
            return {
                ...state,
                lastLevelIndex: newLast,
                levelId: action.payload,
                mapLevels: all,
                mapTransition: false,
            };
        case ActionTypes.UPDATE_MAP_DATA:
            const levels = { ...state.mapLevels };

            const l = { ...levels[action.payload.id] };
            const stars =
                typeof action.payload.stars === 'string'
                    ? parseInt(action.payload.stars, 10)
                    : action.payload.stars;

            const levelStars = l.stars;
            const maxStars = Math.max(stars, levelStars);
            const gainedStars = levelStars < maxStars ? maxStars - levelStars : 0;
            const total = state.totalStars + gainedStars;
            l.stars = maxStars;
            levels[action.payload.id] = { ...l };
            return {
                ...state,
                gainedStars,
                mapLevels: levels,
                totalStars: total,
            };
        case ActionTypes.SAVE_PURCHASE:
            return {
                ...state,
                purchases: [...state.purchases, action.payload],
            };
        case ActionTypes.SET_ACTIVE_COLLECTION:
            return {
                ...state,
                activeCollection: action.payload,
            };
        case ActionTypes.SET_COLLECTABLES_PROGRESS:
            const [collectionId, progress] = action.payload;
            const progressList = { ...state.collectablesProgress };
            const currentTokens = progressList[collectionId] || 0;
            const gainedTokens = (state.gainedTokens || 0) + (progress - currentTokens);
            progressList[collectionId] = progress;
            return {
                ...state,
                gainedTokens,
                gainedTokensCollection: collectionId,
                collectablesProgress: progressList,
            };
        case ActionTypes.SET_PRICE_WHEEL_COOLDOWN:
            return {
                ...state,
                prizeWheelCooldown: action.payload,
            };
        case ActionTypes.TRANSITION_MAP:
            return {
                ...state,
                mapTransition: true,
            };
        case ActionTypes.UPDATE_COINS:
            const gainedCoins = (state.gainedCoins || 0) + action.payload;
            return {
                ...state,
                gainedCoins,
            };
        case ActionTypes.UPDATE_HEARTS:
            const gainedHearts = (state.gainedHearts || 0) + action.payload;
            return {
                ...state,
                gainedHearts,
            };
        case ActionTypes.STORE_GAME_BOOSTER:
            const gBooster = state.gainedBoosters;
            action.payload.forEach(booster => {
                gBooster.push(booster);
            });
            return {
                ...state,
                gainedBoosters: gBooster,
            };
        case ActionTypes.REMOVE_STORED_BOOSTER:
            const gainedBoosters = state.gainedBoosters;
            const storedIndex = gainedBoosters.indexOf(action.payload);
            if (storedIndex >= 0) {
                gainedBoosters.splice(storedIndex, 1);
            }
            return {
                ...state,
                gainedBoosters,
            };
        case ActionTypes.START_LEVEL:
            const startLevels = { ...state.mapLevels };
            if (!startLevels[action.payload]) {
                const newLevel = { id: action.payload, stars: 0 };
                startLevels[action.payload] = newLevel;
            }

            const levelToAddAttempt = startLevels[action.payload];
            if (!levelToAddAttempt.attempts) {
                levelToAddAttempt.attempts = 1;
            } else {
                levelToAddAttempt.attempts++;
            }

            const n = Math.max(state.lastLevelIndex, getLevelIndex(action.payload));
            return {
                ...state,
                gainedStars: 0,
                gainedBoosters: [],
                gainedCoins: 0,
                gainedHearts: 0,
                levelId: action.payload,
                mapLevels: startLevels,
                replaying: isReplay(state, action.payload),
                lastLevelIndex: n, // for debug
            };
        case ActionTypes.VISIT_MAP:
            return {
                ...state,
                visited: addToVisited(state, action.payload),
            };
        case ActionTypes.XPROMO_DISMISSED:
            return {
                ...state,
                xPromoDismissed: action.payload,
            };

        case ActionTypes.OPT_IN_SHORTCUT_REWARDED:
            return {
                ...state,
                optInShortcutRewardDate: Date.now(),
            };
        case ActionTypes.OPT_IN_SHORTCUT_ACCEPTED:
            return {
                ...state,
                optInShortcutShown: false,
            };
        case ActionTypes.OPT_IN_SHORTCUT_SHOWN:
            return {
                ...state,
                nativeOptInShortcutRequired: false,
                optInShortcutShown: true,
            };
        case ActionTypes.OPT_IN_SHORTCUT_DISMISSED:
            return {
                ...state,
                optInShortcutShown: false,
                nativeOptInShortcutRequired: false,
            };
        case ActionTypes.NATIVE_OPT_IN_SHORTCUT_PROMT:
            return {
                ...state,
                nativeOptInShortcutRequired: true,
            };
        case ActionTypes.SHOP_OPENED:
            return {
                ...state,
                shopHasBeenOpened: true,
            };
        case ActionTypes.SHOP_GIFT:
            return {
                ...state,
                shopGift: action.payload,
            };

        default:
            return state;
    }
};

function addToVisited(state: IStatePlayer, map: string) {
    const visited = [...state.visited];
    if (!visited.includes(map)) {
        visited.push(map);
    }
    return visited;
}

function createGameMaps(): IMapData {
    const playerRestore = createDefaultMaps();
    return playerRestore;
}

function createDefaultMaps(): IMapData {
    return {};
}

export function convertMapPlaceToScore(levelId: string) {
    const index = parseInt(levelId.substr(levelId.indexOf('-') + 1), 10);
    const map = levelId.substr(0, levelId.indexOf('-'));
    const mapIndex = MapList.indexOf(map);

    return (mapIndex + 1) * 10000 + index;
}

export const isReplay = (state: IStatePlayer, levelId: string) => {
    // disable for editor
    if (!state.mapLevels[levelId]) {
        return false;
    }
    return state.mapLevels[levelId].stars !== 0;
};
